import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { APP } from "@/constants/testIds";
import { Sparkles, Send, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Einstein() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    const { data } = await api.get("/einstein/sessions");
    setSessions(data.sessions || []);
    if (!sessionId && data.sessions?.length) {
      loadSession(data.sessions[0].session_id);
    }
  };

  const loadSession = async (id) => {
    setSessionId(id);
    const { data } = await api.get(`/einstein/history/${id}`);
    setMessages(data.messages || []);
  };

  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text, id: Date.now().toString() }]);
    setLoading(true);
    try {
      const { data } = await api.post("/einstein/chat", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, id: data.session_id + "-a" + Date.now() }]);
      if (!sessionId) loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI request failed");
    } finally {
      setLoading(false);
    }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
  };

  return (
    <div className="grid h-[calc(100vh-140px)] grid-cols-[240px_1fr] gap-4">
      {/* Sessions */}
      <aside className="flex flex-col rounded-lg border border-stone-200 bg-white">
        <div className="border-b border-stone-200 p-3">
          <button data-testid={APP.einsteinNewSession} onClick={newSession} className="flex w-full items-center gap-2 rounded-md bg-slate-950 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800">
            <Plus className="h-4 w-4" /> New conversation
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <div className="label-mono px-2 py-2">Recent</div>
          {sessions.length === 0 && <div className="px-2 py-4 text-xs text-stone-500">No conversations yet.</div>}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => loadSession(s.session_id)}
              className={`block w-full rounded-md px-2 py-2 text-left text-xs transition-colors ${s.session_id === sessionId ? "bg-stone-100 text-slate-950" : "text-stone-600 hover:bg-stone-50"}`}
            >
              <div className="truncate font-medium">Session {s.session_id.slice(0, 8)}</div>
              <div className="mt-0.5 font-mono text-[10px] text-stone-500">{s.count} messages</div>
            </button>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <div className="flex flex-col rounded-lg border border-stone-200 bg-white">
        <div className="flex items-center gap-3 border-b border-stone-200 px-6 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-950 text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display text-lg font-medium text-slate-950">Business Einstein™</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">Your persistent AI advisor</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6">
          {messages.length === 0 && (
            <div className="mx-auto max-w-lg text-center">
              <Sparkles className="mx-auto h-8 w-8 text-slate-300" />
              <h3 className="mt-4 font-display text-xl font-medium text-slate-950">Ask me anything about your business.</h3>
              <p className="mt-2 text-sm text-stone-600">I know your Business DNA, every plan, every score, every conversation. Try:</p>
              <div className="mt-6 grid gap-2 text-left">
                {[
                  "What are the biggest risks to my business right now?",
                  "How should I prioritize the next 30 days?",
                  "What grants might I be a strong match for?",
                  "Help me craft a 60-second elevator pitch.",
                ].map((p) => (
                  <button key={p} onClick={() => setInput(p)} className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-slate-950 hover:bg-white">
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mx-auto max-w-3xl space-y-6">
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-slate-950 text-white">
                    <Sparkles className="h-4 w-4" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-lg px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-slate-950 text-white" : "border border-stone-200 bg-white text-slate-950"}`}>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-slate-950 text-white">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-950" />
                  <span className="text-sm text-stone-600">Business Einstein is thinking…</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={send} className="border-t border-stone-200 p-4">
          <div className="mx-auto flex max-w-3xl gap-2">
            <textarea
              data-testid={APP.einsteinInput}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder="Ask Business Einstein…"
              className="flex-1 resize-none rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-slate-950 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
            />
            <button data-testid={APP.einsteinSend} type="submit" disabled={loading || !input.trim()} className="btn-primary !px-4">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
